import {
  ExternalFeatureLayer,
  FeatureLayerGeomType,
  MapboxFeature,
  MapDrawerEntityForPoi,
} from 'features/mapping/mapping.types';
import {
  ExternalFeatureLayerMetadata,
  ExternalFeatureProperties,
} from './ExternalFeatureLayer.types';

export const getLayerMetadata = (
  featureLayer: ExternalFeatureLayer,
  interactive = true,
): ExternalFeatureLayerMetadata => {
  return {
    cardDisplayType: featureLayer.infoCardDisplayStyleData.displayStyleType,
    layerOrderSlot: featureLayer.layerOrderSlot,
    displayName: featureLayer.displayName,
    interactive,
  };
};

export const getSimpleStylePointClickListenerLayerId = (
  sourceId: string,
): string => {
  return `${sourceId}_point`;
};

export const getSimpleStyleLineClickListenerLayerId = (
  sourceId: string,
): string => {
  return `${sourceId}_line`;
};

export const getSimpleStylePolygonClickListenerLayerId = (
  sourceId: string,
): string => {
  return `${sourceId}_fill`;
};

export const getSimpleStyleClickListenerLayerId = (
  sourceId: string,
  geomType: FeatureLayerGeomType,
): string | null => {
  if (geomType === 'point') {
    return getSimpleStylePointClickListenerLayerId(sourceId);
  }
  if (geomType === 'polygon') {
    return getSimpleStylePolygonClickListenerLayerId(sourceId);
  }
  if (geomType === 'line') {
    return getSimpleStyleLineClickListenerLayerId(sourceId);
  }
  return null;
};

export const getSelectedLayerId = (
  sourceId: string,
  mapDrawerEntityForPoi: MapDrawerEntityForPoi | null,
): string | number =>
  (Boolean(mapDrawerEntityForPoi) &&
    'sourceId' in mapDrawerEntityForPoi!.metadata &&
    mapDrawerEntityForPoi!.metadata.sourceId === sourceId &&
    mapDrawerEntityForPoi!.id) ||
  '';

/**
 * Maps a single clicked external-feature-layer feature to its drawer entity.
 */
export const getExternalFeatureDrawerEntity = (
  feature: MapboxFeature<ExternalFeatureProperties>,
): MapDrawerEntityForPoi => {
  const { cardDisplayType, layerOrderSlot, displayName } = feature.layer
    ?.metadata as ExternalFeatureLayerMetadata;

  if (cardDisplayType === 'overstory') {
    return {
      id: feature.id,
      type: 'overstory',
      metadata: {
        sourceId: feature.source!,
        featureId: feature.id,
        layerOrderSlot,
      },
    };
  }

  return {
    id: feature.id,
    type: 'basic',
    metadata: {
      sourceId: feature.source!,
      featureId: feature.id,
      layerOrderSlot,
      displayName,
    },
  };
};

/**
 * De-duplicates clicked features. Per Mapbox, feature geometries may be split
 * or duplicated across tile boundaries, so queryRenderedFeatures can return the
 * same feature multiple times. Feature ids are only unique within a layer, so we
 * key by `source + id` (source is the unique mapLayerId) to avoid dropping
 * distinct features that share an id across layers.
 *
 * Features without an id are skipped: they can't be highlighted (getSelectedLayerId)
 * or fetched (useExternalFeatureQuery is keyed on featureId), so they aren't
 * actionable. In practice these layers always carry ids (unlikely edge case).
 */
export const dedupeExternalFeatures = (
  features: MapboxFeature<ExternalFeatureProperties>[],
): MapboxFeature<ExternalFeatureProperties>[] => {
  const seen = new Set<string>();

  return features.filter((feature) => {
    if (feature.id == null) return false;
    const key = `${feature.source}:${feature.id}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};
